import { cx } from '@flowus/common/cx';
import { BlockStatus, BlockType, PermissionRole } from '@next-space/fe-api-idl';
import type { Timeout } from 'ahooks/lib/useRequest/src/types';
import type { FC } from 'react';
import { memo, useEffect, useRef } from 'react';
import { LoadingContainer } from 'src/common/components/loading-container';
import { UUID_NAMESPACE } from 'src/common/const';
import * as Empty from 'src/components/empty';
import { ImagesProvider } from 'src/components/images-provider';
import { ServiceCenter } from 'src/components/service-center';
import { DragPreview } from 'src/editor/editor/plugin/dnd/preview';
import { EditableNamespace } from 'src/editor/editor/uikit/editable-context';
import { useFetchAIPrompt } from 'src/editor/editor/uikit/prompt-editor/use-fetch-ai-prompt';
import { LockedContext, useBlockLocked } from 'src/hooks/block/use-block-locked';
import { useExpandToc } from 'src/hooks/block/use-navs';
import { useScheduleSyncBlock } from 'src/hooks/block/use-schedule-sync-block';
import { useFetchPage, useSubPages } from 'src/hooks/page';
import { usePageMeta } from 'src/hooks/page/use-page-meta';
import { useListenMembersStateInPage } from 'src/hooks/public/use-listen-members-state';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useCurrentSpace } from 'src/hooks/space';
import { HISTORY_CLEAR } from 'src/redux/actions';
import { cache, dispatch } from 'src/redux/store';
import { addFetchPageCompleteId, useFetchPageCompleteIds } from 'src/services/app-support';
import {
  $appUiStateCache,
  setAppUiState,
  useGetOffline,
  usePageRootLoaded,
  useSetIsEditingPage,
} from 'src/services/app/hook';
import { useGetSyncService } from 'src/services/sync';
import { $searchParams } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { useIsInRight } from 'src/utils/right-utils';
import { PageDocEmpty } from '../empty/page-doc-empty';
import { PageOfflineEmpty } from '../empty/page-offline-empty';
import { Drawer } from './drawer/drawer';
import { Header } from './header';
import { Page } from './page';
import { PageScene, PageSceneContext } from './scene-context';

export const MainContent: FC<{ uuid: string }> = memo(({ uuid }) => {
  useListenMembersStateInPage();
  useFetchAIPrompt();
  const sync = useGetSyncService();
  const { role } = usePermissions(uuid);
  const locked = useBlockLocked(uuid);
  const pageMeta = usePageMeta(uuid);
  const fetchPage = useFetchPage();
  const expandToc = useExpandToc();
  const isInRight = useIsInRight();
  const currentSpace = useCurrentSpace();
  const rootPages = useSubPages(currentSpace.uuid);
  const pageRootLoaded = usePageRootLoaded();
  const fetchPageCompleteIds = useFetchPageCompleteIds();
  const offline = useGetOffline();
  // 避免创建新block后需要拉block的数据，但由于网络原因后端还没有生成block导致显示找不到block
  // 加一个定时器，延迟后再同意去拉取这个block的数据
  const localBlockUploadTimer = useRef<Timeout | null>(null);
  const canGetBlockData = useRef(true);
  const setIsEditingPage = useSetIsEditingPage();

  useEffect(() => {
    const updateLoadPageList = () => {
      addFetchPageCompleteId(uuid);
    };

    if (!uuid) return;
    // 要有uuid才清空，否则清空模板中心无法undo
    dispatch(HISTORY_CLEAR());
    const block = cache.blocks[uuid];

    // 这里拿到local为true时，不执行后面的代码，新建的页面不需要fetch
    // 更新local在page.tsx里
    if (block?.local) {
      localBlockUploadTimer.current && clearTimeout(localBlockUploadTimer.current);
      localBlockUploadTimer.current = setTimeout(() => {
        canGetBlockData.current = true;
      }, 1000);
      updateLoadPageList();
      canGetBlockData.current = false;
      return;
    }

    if (!canGetBlockData.current) {
      return;
    }

    bizTracker.pageEnter('page_enter_time');

    void fetchPage(uuid).finally(() => {
      const { blocks } = cache;
      const block = blocks[uuid];
      if (!block) return;
      if (block.type === BlockType.CHART || block.type === BlockType.CHART_PAGRE) {
        block.data.format?.chartFormat?.ref?.uuid &&
          void fetchPage(block.data.format?.chartFormat?.ref?.uuid);
      } else if (
        block.type === BlockType.REFERENCE_COLLECTION ||
        block.type === BlockType.REFERENCE_COLLECTION_PAGE
      ) {
        const collectionId = block.data.ref?.uuid;
        if (collectionId) {
          void fetchPage(collectionId).finally(() => {
            updateLoadPageList();
            if (
              block.status === BlockStatus.NORMAL &&
              !isInRight &&
              $appUiStateCache.$firstLoadPage
            ) {
              expandToc(collectionId);
            }

            setAppUiState({ $firstLoadPage: false });
          });
        } else {
          updateLoadPageList();
        }
      } else {
        updateLoadPageList();
        if (block.status === BlockStatus.NORMAL && !isInRight && $appUiStateCache.$firstLoadPage) {
          // 首次加载页面，需要展开对应的目录树
          expandToc(uuid);
        }
        setAppUiState({ $firstLoadPage: false });
      }

      bizTracker.pageLeave('page_enter_time', { page_type: block.type });
    });
    // NOTE(by yuxiang & 少侠): 请不要去掉 pageMeta?.timestamp。这个是用来在恢复历史版本后重新加载用的
  }, [expandToc, fetchPage, isInRight, uuid, pageMeta?.timestamp]);

  const scheduleSyncBlock = useScheduleSyncBlock();
  useEffect(() => {
    return sync.subscribePageBlocks(uuid, (packet) => {
      if (!packet) {
        // 短线重连需要全量拉一次
        void fetchPage(uuid);
        return;
      }
      scheduleSyncBlock(packet.data.id);
    });
  }, [sync, uuid, fetchPage, scheduleSyncBlock]);

  useEffect(() => {
    const handleMouseMove = () => {
      if (!$appUiStateCache.$isEditingPage) return;
      setIsEditingPage(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [setIsEditingPage]);

  // 访问首页的时候右侧空页面会一闪而过,rootSpace接口没加载完毕就处于loading状态
  const isEmptySpace = !uuid && pageRootLoaded && rootPages.length === 0;

  // 如果空页面没有这个判断，会一直处于loading态度
  const isCacheExpired = pageMeta?.tag === 'CACHE_EXPIRED';
  const needLoading =
    !fetchPageCompleteIds[uuid] && !isEmptySpace && !pageMeta?.tag && !cache.blocks[uuid]?.local;

  const loading = isCacheExpired || needLoading;

  // 离线页面提示
  if (offline && loading) {
    return (
      <>
        <Header />
        <PageOfflineEmpty />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <Header />
        <LoadingContainer className="flex-1" />
      </>
    );
  }

  // 这个必须在前面，否则会先显示无权限页面
  if (isEmptySpace) {
    return (
      <>
        <Header />
        <PageDocEmpty />
        <ServiceCenter />
      </>
    );
  }

  if (pageMeta?.tag === 'NOT_FOUND') {
    return (
      <>
        <Header />
        <Empty.NotFound showBackButton={false} />
      </>
    );
  }

  if (pageMeta?.tag === 'NO_PERMISSION' || role === PermissionRole.NONE) {
    return (
      <>
        <Header />
        <Empty.NotShared />
      </>
    );
  }

  return (
    <PageSceneContext.Provider value={PageScene.MAIN}>
      <EditableNamespace.Provider value={isInRight ? UUID_NAMESPACE.RIGHT_PAGE : undefined}>
        <Header key={uuid} />
        <div className={cx(!$searchParams.print && 'flex-1 min-h-0 flex')}>
          <LockedContext.Provider value={Boolean(locked)}>
            <ImagesProvider uuid={uuid}>
              <Page uuid={uuid} />
            </ImagesProvider>
          </LockedContext.Provider>
          <Drawer />
          <DragPreview />
        </div>
        <ServiceCenter />
      </EditableNamespace.Provider>
    </PageSceneContext.Provider>
  );
});
